export const M2_UNIT_2 = {
  id: "m2-u2",
  title: "Unit 2: Short Answers",
  topic: "Respuestas Cortas (Yes, I am / No, I don't)",
  iconType: "map",
  grammarImage: "/m2-u2-grammar.jpg",
  grammarText: "Regla de Oro: Escucha la primera palabra de la pregunta. \n¿Empieza con **Are**? -> Yes, I **am**. \n¿Empieza con **Do**? -> Yes, I **do**.",
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: VERB TO BE (Ser/Estar) - Identity & Description
    // Enfoque: Are you? Is she? -> I am / She is.
    // ----------------------------------------------------------------------
    {
      id: "u18-set1",
      title: "Set 1: Answers with 'To Be'",
      questions: [
        { 
          type: "mc", 
          q: "Are you a doctor?", 
          options: ["Yes, I am.", "Yes, I do.", "No, you aren't."], 
          a: "Yes, I am.",
          tip: "Si preguntan con **Are**, la respuesta usa el verbo to be (**am**)."
        },
        { 
          type: "mc", 
          q: "Is she French?", 
          options: ["No, she's not.", "No, she doesn't.", "Yes, she does."], 
          a: "No, she's not.",
          tip: "La pregunta usa **Is**, así que la respuesta debe mantener **is** (o 's not)."
        },
        { 
          type: "blank", 
          q: "Are they students? Yes, ___ are.", 
          options: ["they", "them"], 
          a: "they",
          tip: "En respuestas cortas usamos pronombres de sujeto (They), no de objeto (Them)."
        },
        { 
          type: "mc", 
          q: "Is this your laptop?", 
          options: ["Yes, it is.", "Yes, it does.", "No, it's not."], 
          a: "Yes, it is.",
          tip: "Para cosas (laptop), usamos **it**."
        },
        { 
          type: "blank", 
          q: "Is your name Sophie? No, ___ isn't.", 
          options: ["it", "he"], 
          a: "it",
          tip: "Tu nombre es una 'cosa' o concepto, así que usamos **it**."
        },
        { 
          type: "order", 
          q: "Ordena: Yes, I am a teacher.", 
          words: ["Yes,", "I", "am", "a", "teacher."], 
          a: "Yes, I am a teacher." 
        },
        { 
          type: "mc", 
          q: "Are you Chinese?", 
          options: ["No, I'm not.", "No, I don't."], 
          a: "No, I'm not.",
          tip: "Nunca mezcles 'Are' con 'Don't'."
        },
        { 
          type: "blank", 
          q: "Is he your brother? Yes, ___ is.", 
          options: ["he", "him"], 
          a: "he" 
        },
        { 
          type: "order", 
          q: "Ordena la respuesta negativa:", 
          words: ["No,", "it", "isn't", "my", "bag."], 
          a: "No, it isn't my bag." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: AUXILIARIES DO/DOES (Action)
    // Enfoque: Rutinas y Acciones.
    // ----------------------------------------------------------------------
    {
      id: "u18-set2",
      title: "Set 2: Answers with 'Do/Does'",
      questions: [
        { 
          type: "mc", 
          q: "Do you work in an office?", 
          options: ["Yes, I do.", "Yes, I am.", "No, I'm not."], 
          a: "Yes, I do.",
          tip: "La pregunta empieza con **Do**, así que respondes con **do**."
        },
        { 
          type: "mc", 
          q: "Does she live here?", 
          options: ["No, she doesn't.", "No, she isn't.", "Yes, she is."], 
          a: "No, she doesn't.",
          tip: "Con 'She' y verbos de acción, el negativo es **doesn't**."
        },
        { 
          type: "blank", 
          q: "Do they play tennis? Yes, ___ do.", 
          options: ["they", "them"], 
          a: "they" 
        },
        { 
          type: "mc", 
          q: "Does he get up early?", 
          options: ["Yes, he does.", "Yes, he is.", "No, he's not."], 
          a: "Yes, he does.",
          tip: "Fíjate en el auxiliar: **Does** he...? -> Yes, he **does**."
        },
        { 
          type: "blank", 
          q: "Do you watch TV? No, ___ don't.", 
          options: ["I", "me"], 
          a: "I" 
        },
        { 
          type: "order", 
          q: "Ordena: No, they don't live here.", 
          words: ["No,", "they", "don't", "live", "here."], 
          a: "No, they don't live here." 
        },
        { 
          type: "mc", 
          q: "Does Joe watch TV?", 
          options: ["Yes, he does.", "Yes, it does.", "No, he doesn't."], 
          a: "Yes, he does.",
          tip: "Joe es 'He', así que usamos **he does**."
        },
        { 
          type: "blank", 
          q: "Do they speak English? No, ___ don't.", 
          options: ["they", "them"], 
          a: "they" 
        },
        { 
          type: "order", 
          q: "Ordena la afirmación:", 
          words: ["Yes,", "I", "do", "eat", "breakfast."], 
          a: "Yes, I do eat breakfast." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: MIXING CONTEXTS (Jobs & Routines)
    // Enfoque: Distinguir si te preguntan "qué eres" o "qué haces".
    // ----------------------------------------------------------------------
    {
      id: "u18-set3",
      title: "Set 3: Job or Action?",
      questions: [
        { 
          type: "mc", 
          q: "Are you a receptionist?", 
          options: ["Yes, I am.", "Yes, I do."], 
          a: "Yes, I am.",
          tip: "Te preguntan qué *eres* (To Be), no qué haces."
        },
        { 
          type: "mc", 
          q: "Does she work in an office?", 
          options: ["Yes, she does.", "Yes, she is."], 
          a: "Yes, she does.",
          tip: "El verbo es 'work' (acción), así que usamos **does**."
        },
        { type: "blank", q: "Is he a nurse? No, ___'s not.", options: ["he", "him"], a: "he" },
        { 
          type: "mc", 
          q: "Do you start work at 9am?", 
          options: ["Yes, I do.", "Yes, I am."], 
          a: "Yes, I do.",
          tip: "Pregunta sobre rutina -> **Do**."
        },
        { 
          type: "blank", 
          q: "Are they engineers? No, they ___n't.", 
          options: ["aren't", "don't"], 
          a: "aren't",
          tip: "Negativo de 'Are' -> **Aren't**."
        },
        { type: "order", q: "Ordena:", words: ["Yes,", "she", "works", "five", "days", "a", "week."], a: "Yes, she works five days a week." },
        { type: "mc", q: "Does Maria like her job?", options: ["No, she doesn't.", "No, she's not."], a: "No, she doesn't." },
        { type: "mc", q: "Is she from Poland?", options: ["Yes, she is.", "Yes, she does."], a: "Yes, she is." },
        { type: "blank", q: "Do you work with animals? No, ___ don't.", options: ["I"], a: "I" },
        { type: "order", q: "Ordena:", words: ["No,", "I'm", "not", "American."], a: "No, I'm not American." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: LOGIC SELECTION
    // Enfoque: Elegir la respuesta lógica gramaticalmente.
    // ----------------------------------------------------------------------
    {
      id: "u18-set4",
      title: "Set 4: Logical Replies",
      questions: [
        { type: "mc", q: "Are you American?", options: ["Yes, I am.", "Yes, I do."], a: "Yes, I am." },
        { type: "mc", q: "Does Joe watch TV?", options: ["Yes, he does.", "Yes, he is."], a: "Yes, he does." },
        { type: "blank", q: "Is your name Maisy? No, it ___.", options: ["isn't"], a: "isn't" },
        { type: "mc", q: "Do they speak English?", options: ["Yes, they do.", "Yes, they are."], a: "Yes, they do." },
        { type: "blank", q: "Is that your house? No, it ___.", options: ["isn't"], a: "isn't" },
        { 
          type: "order", 
          q: "Responde: Does she play tennis?", 
          words: ["Yes,", "she", "does."], 
          a: "Yes, she does." 
        },
        { 
          type: "mc", 
          q: "Are you a student?", 
          options: ["No, I'm not.", "No, I don't."], 
          a: "No, I'm not.",
          tip: "Si no eres estudiante (estado), usas **I'm not**."
        },
        { type: "mc", q: "Does she work here?", options: ["Yes, she does.", "Yes, she is."], a: "Yes, she does." },
        { type: "blank", q: "Do you eat breakfast? No, I ___.", options: ["don't"], a: "don't" },
        { 
          type: "order", 
          q: "Responde: Is Miranda your aunt?", 
          words: ["No,", "she", "isn't."], 
          a: "No, she isn't." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: FILL IN THE BLANKS (Production)
    // Enfoque: Completar la respuesta corta.
    // ----------------------------------------------------------------------
    {
      id: "u18-set5",
      title: "Set 5: Complete the Answer",
      questions: [
        { type: "blank", q: "Are you a doctor? Yes, ___ am.", options: ["I"], a: "I" },
        { type: "blank", q: "Does he live here? No, ___ doesn't.", options: ["he"], a: "he" },
        { type: "mc", q: "Is your cat black?", options: ["No, it isn't.", "No, it doesn't."], a: "No, it isn't." },
        { type: "blank", q: "Do they work here? Yes, ___ do.", options: ["they"], a: "they" },
        { type: "blank", q: "Are you from Canada? No, ___'m not.", options: ["I"], a: "I" },
        { 
          type: "order", 
          q: "Responde: Does Peter speak French?", 
          words: ["No,", "he", "doesn't."], 
          a: "No, he doesn't." 
        },
        { type: "mc", q: "Is Paula your wife? Yes, ___ is.", options: ["she", "her"], a: "she" },
        { type: "blank", q: "Do you play golf? No, ___ don't.", options: ["I"], a: "I" },
        { type: "mc", q: "Is Mario from Italy?", options: ["Yes, he is.", "Yes, he does."], a: "Yes, he is." },
        { 
          type: "order", 
          q: "Responde: Do they work at the factory?", 
          words: ["No,", "they", "don't."], 
          a: "No, they don't." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: MIXED CHALLENGE (Agilidad Mental)
    // Enfoque: Alternar rápidamente entre sistemas.
    // ----------------------------------------------------------------------
    {
      id: "u18-set6",
      title: "Set 6: Quick Switch",
      questions: [
        { 
          type: "mc", 
          q: "Are you happy?", 
          options: ["Yes, I am.", "Yes, I do."], 
          a: "Yes, I am." 
        },
        { 
          type: "mc", 
          q: "Does she dance?", 
          options: ["Yes, she does.", "Yes, she is."], 
          a: "Yes, she does." 
        },
        { 
          type: "order", 
          q: "Responde: Is this your cat?", 
          words: ["No,", "it", "isn't."], 
          a: "No, it isn't." 
        },
        { type: "blank", q: "Do you live in Chicago? Yes, ___ do.", options: ["I"], a: "I" },
        { 
          type: "mc", 
          q: "Is your name Stefan?", 
          options: ["No, it isn't.", "No, he doesn't."], 
          a: "No, it isn't.",
          tip: "Tu nombre es 'It'."
        },
        { type: "blank", q: "Are they your children? No, they ___n't.", options: ["aren't"], a: "aren't" },
        { type: "mc", q: "Do you work here?", options: ["Yes, I do.", "Yes, I am."], a: "Yes, I do." },
        { 
          type: "order", 
          q: "Responde: Does she finish at 4pm?", 
          words: ["Yes,", "she", "does."], 
          a: "Yes, she does." 
        },
        { type: "blank", q: "Is that your house? No, ___ isn't.", options: ["it"], a: "it" },
        { type: "mc", q: "Do they work at the factory?", options: ["No, they don't.", "No, they aren't."], a: "No, they don't." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: ERROR CORRECTION (Análisis)
    // Enfoque: Identificar la respuesta incorrecta.
    // ----------------------------------------------------------------------
    {
      id: "u18-set7",
      title: "Set 7: Spot the Mistake",
      questions: [
        { 
          type: "mc", 
          q: "Q: Are you French? \nA: Yes, I do. (Incorrect)", 
          options: ["Correct: Yes, I am.", "Correct: Yes, I have."], 
          a: "Correct: Yes, I am.",
          tip: "Nunca respondas a 'Are you...?' con 'I do'. Usa 'I am'."
        },
        { 
          type: "blank", 
          q: "Q: Does she work? \nA: No, she isn't. \nFix: No, she ___.", 
          options: ["doesn't"], 
          a: "doesn't",
          tip: "Pregunta con 'Does' -> Respuesta con 'Doesn't'."
        },
        { 
          type: "mc", 
          q: "Q: Is he a teacher? \nA: Yes, he do.", 
          options: ["Fix: Yes, he is.", "Fix: Yes, he does."], 
          a: "Fix: Yes, he is." 
        },
        { 
          type: "order", 
          q: "Fix the answer: 'No, I'm not' (for 'Do you eat?')", 
          words: ["No,", "I", "don't."], 
          a: "No, I don't." 
        },
        { type: "blank", q: "Q: Are they doctors? \nA: No, they do not. \nFix: No, they ___n't.", options: ["aren't"], a: "aren't" },
        { 
          type: "mc", 
          q: "Q: Is it yours? \nA: Yes, it isn't. (Logic Error)", 
          options: ["Yes, it is.", "No, it is."], 
          a: "Yes, it is." 
        },
        { type: "mc", q: "Q: Does he play? \nFix: No, he ___.", options: ["doesn't", "isn't"], a: "doesn't" },
        { type: "blank", q: "Q: Are you happy? \nFix: No, ___'m not.", options: ["I"], a: "I" },
        { type: "order", q: "Fix: 'Yes, they is.'", words: ["Yes,", "they", "are."], a: "Yes, they are." },
        { 
          type: "mc", 
          q: "Q: Do you know? \nA: No, I am not.", 
          options: ["Fix: No, I don't.", "Fix: No, I haven't."], 
          a: "Fix: No, I don't." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: READING COMPREHENSION (Story)
    // Enfoque: Extraer respuestas cortas de un texto.
    // ----------------------------------------------------------------------
    {
      id: "u18-set8",
      title: "Set 8: Reading - Maria's Job",
      questions: [
        { 
          type: "mc", 
          q: "READING:\n'Maria is from Poland. She is a receptionist.'\n\nQ: Is Maria from Poland?", 
          options: ["Yes, she is.", "Yes, she does."], 
          a: "Yes, she is." 
        },
        { 
          type: "blank", 
          q: "She works in an office. Does she work in a school? No, she ___.", 
          options: ["doesn't"], 
          a: "doesn't" 
        },
        { 
          type: "mc", 
          q: "READING:\n'She doesn't like the traffic, but she loves her job.'\n\nQ: Does she like her job?", 
          options: ["Yes, she does.", "No, she doesn't."], 
          a: "Yes, she does.",
          tip: "El texto dice 'she loves her job'."
        },
        { 
          type: "mc", 
          q: "READING:\n'She starts work at 9am.'\n\nQ: Does she start at 8am?", 
          options: ["No, she doesn't.", "Yes, she does."], 
          a: "No, she doesn't." 
        },
        { type: "blank", q: "READING: 'She works five days a week.' \nQ: Does she work on weekends? No, she ___.", options: ["doesn't"], a: "doesn't" },
        { 
          type: "order", 
          q: "Responde: Is she a receptionist?", 
          words: ["Yes,", "she", "is."], 
          a: "Yes, she is." 
        },
        { 
          type: "mc", 
          q: "READING:\n'She walks to work.'\n\nQ: Does she drive?", 
          options: ["No, she doesn't.", "Yes, she does."], 
          a: "No, she doesn't." 
        },
        { 
          type: "blank", 
          q: "READING:\n'She finishes at 4pm.'\n\nQ: Does she finish at 5pm? No, ___ doesn't.", 
          options: ["she"], 
          a: "she" 
        },
        { type: "mc", q: "Is Maria happy? (Based on 'she loves her job')", options: ["Yes, she is.", "No, she isn't."], a: "Yes, she is." },
        { 
          type: "order", 
          q: "Responde: Does she work five days?", 
          words: ["Yes,", "she", "does."], 
          a: "Yes, she does." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 9: CONVERSATION LOGIC
    // Enfoque: Emparejar preguntas con respuestas lógicas.
    // ----------------------------------------------------------------------
    {
      id: "u18-set9",
      title: "Set 9: Logical Matches",
      questions: [
        { 
          type: "mc", 
          q: "Are you a student?", 
          options: ["No, I'm a teacher.", "Yes, I do study."], 
          a: "No, I'm a teacher.",
          tip: "Buscamos la respuesta más natural y correcta gramaticalmente."
        },
        { 
          type: "order", 
          q: "Responde: Does he finish early?", 
          words: ["Yes,", "he", "does."], 
          a: "Yes, he does." 
        },
        { type: "blank", q: "Is that your dog? No, ___ isn't mine.", options: ["it"], a: "it" },
        { 
          type: "mc", 
          q: "Do they live nearby? → No, they ___.", 
          options: ["don't", "aren't"], 
          a: "don't" 
        },
        { 
          type: "blank", 
          q: "Are you tired? Yes, I ___.", 
          options: ["am"], 
          a: "am",
          tip: "En inglés no decimos 'I really am' como respuesta corta estándar, mejor solo 'I am' o 'Yes, very'."
        },
        { 
          type: "mc", 
          q: "Match: \n1. Does she cook? \n2. Is he tall?", 
          options: ["1-> No, she doesn't. / 2-> Yes, he is.", "1-> No, she's not. / 2-> Yes, he does."], 
          a: "1-> No, she doesn't. / 2-> Yes, he is." 
        },
        { type: "order", q: "Responde: Do you have kids?", words: ["No,", "we", "don't."], a: "No, we don't." },
        { type: "blank", q: "Is this correct? Yes, ___ is.", options: ["it"], a: "it" },
        { type: "mc", q: "Does your brother work?", options: ["Yes, he does.", "Yes, he's working."], a: "Yes, he does." },
        { type: "order", q: "Responde: Are they friends?", words: ["Yes,", "they", "are."], a: "Yes, they are." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 10: MASTERY CHALLENGE
    // Enfoque: Todo mezclado sin piedad.
    // ----------------------------------------------------------------------
    {
      id: "u18-set10",
      title: "Set 10: Short Answer Master",
      questions: [
        { 
          type: "mc", 
          q: "Q: Are you a teacher? \nA: ___", 
          options: ["Yes, I am.", "Yes, I do."], 
          a: "Yes, I am." 
        },
        { type: "blank", q: "Does she work late? No, she ___.", options: ["doesn't"], a: "doesn't" },
        { 
          type: "order", 
          q: "Responde: Do you like it?", 
          words: ["Yes,", "I", "do."], 
          a: "Yes, I do." 
        },
        { 
          type: "mc", 
          q: "1. Is he your uncle? \n2. Do they travel?", 
          options: ["1-No, he isn't. 2-Yes, they do.", "1-No, he doesn't. 2-Yes, they are."], 
          a: "1-No, he isn't. 2-Yes, they do." 
        },
        { type: "blank", q: "Are you ready? Yes, we ___.", options: ["are"], a: "are" },
        { 
          type: "mc", 
          q: "Quick Check: Does it work?", 
          options: ["No, it doesn't.", "No, it isn't."], 
          a: "No, it doesn't." 
        },
        { type: "order", q: "Responde: Are they here?", words: ["No,", "they", "aren't."], a: "No, they aren't." },
        { type: "blank", q: "Do you understand? Yes, I ___.", options: ["do"], a: "do" },
        { type: "mc", q: "Is she busy?", options: ["Yes, she is.", "Yes, she does."], a: "Yes, she is." },
        { 
          type: "order", 
          q: "Master Challenge: Does your team win?", 
          words: ["Yes,", "they", "do."], 
          a: "Yes, they do." // Asumiendo 'Team' como plural 'they' o singular 'it', 'they' es comun en deportes
        }
      ]
    }
  ]
};